//! 详情字段（详情 Dialog 内统一使用）—— 按 docs/ui_page 模板 03 引用的参考实现移植
//! - 左标签：右对齐，最右侧自动加冒号
//! - 右值：带复制按钮（document.execCommand）
//! - 数组值（items）：默认「列表」美化模式，可一键切换为「源码」(JSON) 模式
//! - 用法：
//!   <DetailField label="Client ID" value={c.clientId} mono />
//!   <DetailField label="Redirect URIs" items={c.redirectUris} mono />
//!   <DetailField label="名称"><Text>{c.name}</Text></DetailField>

import { useState, Children, isValidElement, cloneElement, type ReactNode } from 'react'
import { Text, Button, makeStyles, tokens } from '@fluentui/react-components'
import { InfoTip } from './InfoTip'
import { Copy20Regular, CheckmarkCircle20Regular } from '@fluentui/react-icons'
import dayjs from 'dayjs'

const useStyles = makeStyles({
  field: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '10px 28px',
    alignItems: 'start',
  },
  label: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textAlign: 'right',
    paddingTop: '2px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    justifyContent: 'flex-end',
    width: '100%',
  },
  valueWrap: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
    minWidth: 0,
    fontSize: tokens.fontSizeBase300,
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  valueText: {
    wordBreak: 'break-all',
  },
  // 数组美化列表
  list: {
    margin: 0,
    paddingLeft: '18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  listItem: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: '20px',
    wordBreak: 'break-all',
  },
  listItemMono: {
    fontFamily: 'var(--font-monospace, monospace)',
    fontSize: '13px',
    lineHeight: '20px',
    wordBreak: 'break-all',
  },
  // 数组源码模式（JSON）
  raw: {
    margin: 0,
    background: tokens.colorNeutralBackground2,
    padding: '6px 10px',
    borderRadius: '4px',
    fontFamily: 'var(--font-monospace, monospace)',
    fontSize: '13px',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  copyBtn: {
    minWidth: '24px',
    height: '24px',
    padding: '0',
    flexShrink: 0,
  },
  toggleBtn: {
    minWidth: 'auto',
    height: '24px',
    padding: '0 8px',
    flexShrink: 0,
    fontSize: tokens.fontSizeBase200,
  },
})

export interface DetailFieldProps {
  label: string
  /** 纯文本值（用于展示与复制）。支持 string | number | null | undefined */
  value?: string | number | null
  /** 数组值：默认以「列表」美化展示，可切换为「源码」(JSON) 模式 */
  items?: string[]
  /** 自定义渲染内容（ReactNode），优先于 value */
  children?: ReactNode
  /** 是否使用等宽字体展示（ID / URI / 路径等） */
  mono?: boolean
  /** 字段帮助文案（ReactNode），点击「?」图标弹出 TeachingPopover 解释字段含义 */
  help?: ReactNode
  /** 是否跨满整行（用于长字段：HTML 正文、DNS 记录、描述等）。仅在 DetailGrid 多列布局下生效 */
  colSpan?: boolean
  /** label 列宽（px），默认 140；两列网格（DetailGrid2）下用 100~108 适配半宽 */
  labelWidth?: number
}

export function DetailField({ label, value, items, children, mono, help, colSpan, labelWidth = 140 }: DetailFieldProps) {
  const styles = useStyles()
  const [copied, setCopied] = useState(false)
  // 数组默认进入「列表」美化模式；切换为「raw」即源码（JSON）模式
  const [mode, setMode] = useState<'list' | 'raw'>('list')

  // 标签自动加冒号
  const labelText = label.endsWith(':') ? label : `${label}:`
  // 字段帮助：label 右侧「?」按钮 → 教学气泡（顶栏 InfoLabel 同款）
  const labelNode = help ? (
    <>
      <span>{labelText}</span>
      <InfoTip title={label} content={help} />
    </>
  ) : (
    labelText
  )

  // 详情组件自动把 ISO 时间戳格式化为 YYYY/MM/DD HH:MM:SS（本地时区），避免各页面重复处理
  const displayValue = isLikelyTimestamp(value) ? formatDateTime(value) : value

  const hasItems = Array.isArray(items) && items.length > 0
  const rawText = hasItems ? JSON.stringify(items, null, 2) : ''
  const listText = hasItems ? items.join('\n') : ''

  // 实际要复制的文本：数组按当前模式取；否则取 displayValue / children 纯文本
  const copyText = hasItems
    ? mode === 'raw'
      ? rawText
      : listText
    : displayValue != null
      ? String(displayValue)
      : extractText(children)

  const copyable = !!copyText && copyText !== '—'

  function handleCopy() {
    if (!copyText) return
    copyToClipboard(copyText)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${labelWidth}px 1fr`,
        gridColumn: colSpan ? '1 / -1' : undefined,
        gap: '8px 16px',
        alignItems: 'start',
        minWidth: 0,
      }}
    >
      <Text
        className={styles.label}
        style={labelWidth !== 140 ? { minWidth: `${labelWidth}px` } : undefined}
      >
        {labelNode}
      </Text>
      <div className={styles.valueWrap}>
        {copyable && (
          <Button
            appearance="subtle"
            size="small"
            className={styles.copyBtn}
            onClick={handleCopy}
            title="复制"
            aria-label={`复制 ${label}`}
            icon={copied ? <CheckmarkCircle20Regular /> : <Copy20Regular />}
          />
        )}
        <div className={styles.content}>
          {hasItems ? (
            mode === 'list' ? (
              <ul className={styles.list}>
                {items.map((it, i) => (
                  <li key={i} className={mono ? styles.listItemMono : styles.listItem}>
                    {it || '—'}
                  </li>
                ))}
              </ul>
            ) : (
              <pre className={styles.raw}>{rawText}</pre>
            )
          ) : children != null ? (
            children
          ) : displayValue != null ? (
            mono ? (
              <code>{String(displayValue)}</code>
            ) : (
              <span className={styles.valueText}>{String(displayValue)}</span>
            )
          ) : (
            '—'
          )}
        </div>
        {hasItems && (
          <Button
            appearance="subtle"
            size="small"
            className={styles.toggleBtn}
            onClick={() => setMode((m) => (m === 'list' ? 'raw' : 'list'))}
            title={mode === 'list' ? '切换为源码 (JSON)' : '切换为列表'}
          >
            {mode === 'list' ? '源码' : '列表'}
          </Button>
        )}
      </div>
    </div>
  )
}

/** 详情字段容器（多列自适应网格，宽对话框自动排 2 列充分利用横向空间） */
export function DetailGrid({ children }: { children: ReactNode }) {
  const styles = useStyles()
  // 注入紧凑 labelWidth（104px），让 2 列布局下值有更多空间；colSpan 由 DetailField 自身样式生效
  const items = Children.toArray(children).map((c, i) =>
    isValidElement(c) ? cloneElement(c as React.ReactElement<{ labelWidth?: number }>, { labelWidth: 104, key: i }) : c,
  )
  return <div className={styles.field}>{items}</div>
}

/** 多列详情网格
 *  - cols=2：两列并排，充分利用横向空间；cols=1：单列
 *  - 通过 cloneElement 给每个 DetailField 注入紧凑 labelWidth（避免内联 style 不传 CSS 变量）
 */
export function DetailGrid2({ children, cols = 2, labelWidth }: {
  children: ReactNode
  cols?: 1 | 2
  labelWidth?: number
}) {
  const lw = labelWidth ?? (cols === 1 ? 120 : 104)
  const items = Children.toArray(children).map((c, i) =>
    isValidElement(c) ? cloneElement(c as React.ReactElement<{ labelWidth?: number }>, { labelWidth: lw, key: i }) : c,
  )
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: cols === 1 ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
        gap: cols === 1 ? '8px 16px' : '10px 28px',
        alignItems: 'start',
      }}
    >
      {items}
    </div>
  )
}

/** 从 ReactNode 提取纯文本（用于复制） */
function extractText(node: ReactNode): string {
  if (node == null || node === false) return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractText).join('')
  if (typeof node === 'object' && 'props' in node) {
    return extractText((node as { props: { children?: ReactNode } }).props.children)
  }
  return ''
}

/** 粗略判断是否为 ISO 时间戳字符串（详情组件据此自动格式化） */
function isLikelyTimestamp(v: unknown): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(v)
}

/** 时间戳格式化：YYYY/MM/DD HH:MM:SS（本地时区） */
function formatDateTime(iso: string): string {
  const d = dayjs(iso)
  if (!d.isValid()) return iso
  return d.format('YYYY/MM/DD HH:mm:ss')
}

/** document.execCommand 复制（兼容性最好） */
function copyToClipboard(text: string) {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    document.execCommand('copy')
  } catch {
    // ignore
  }
  document.body.removeChild(textarea)
}